import { z } from 'zod';
import type { TargetPeriodTypeName } from './targets';

// ---------------------------------------------------------------------------------------------
// Fechamento de períodos (T-066, RF-54, RN-06; DEC-004 item 19, DEC-036): o período é o mês da
// empresa. Alterar meta ou resultado de mês fechado exige justificativa e fica marcado na auditoria.

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/** "Mar/2026". */
export const periodMonthLabel = (year: number, month: number) => `${MONTHS[month - 1]}/${year}`;

/** Regras padrão (DEC-004 item 19): fechamento automático ligado, 10 dias após o fim do mês. */
export const DEFAULT_PERIOD_SETTINGS = { autoClose: true, autoCloseDays: 10 } as const;
export const PERIOD_AUTO_CLOSE_MAX_DAYS = 120;

export const periodSettingsSchema = z.object({
  autoClose: z.boolean(),
  autoCloseDays: z.number().int(),
  /** null = a empresa ainda usa o padrão. */
  updatedAt: z.string().nullable(),
});
export type PeriodSettings = z.infer<typeof periodSettingsSchema>;

export const periodSettingsInputSchema = z.object({
  autoClose: z.boolean(),
  autoCloseDays: z.coerce
    .number({ message: 'Informe em quantos dias após o fim do mês.' })
    .int('Use um número inteiro de dias.')
    .min(0, 'Use de 0 a 120 dias.')
    .max(PERIOD_AUTO_CLOSE_MAX_DAYS, 'Use de 0 a 120 dias.'),
});
export type PeriodSettingsInput = z.input<typeof periodSettingsInputSchema>;

export const PERIOD_STATUSES = ['OPEN', 'CLOSED'] as const;
export type PeriodStatusName = (typeof PERIOD_STATUSES)[number];
export const PERIOD_MODES = ['AUTO', 'MANUAL'] as const;
export type PeriodModeName = (typeof PERIOD_MODES)[number];

/** Um mês do ano com a situação de fechamento. */
export const periodMonthSchema = z.object({
  month: z.number().int().min(1).max(12),
  label: z.string(),
  status: z.enum(PERIOD_STATUSES),
  /** Como foi fechado pela última vez (null = nunca fechado). */
  mode: z.enum(PERIOD_MODES).nullable(),
  closedAt: z.string().nullable(),
  closedByName: z.string().nullable(),
  closeReason: z.string().nullable(),
  reopenedAt: z.string().nullable(),
  reopenedByName: z.string().nullable(),
  reopenReason: z.string().nullable(),
  /** O mês já terminou (só mês terminado pode ser fechado). */
  ended: z.boolean(),
  /**
   * Dia em que o fechamento automático vai fechar o mês (null: automático desligado, mês já fechado
   * ou reaberto — reaberto só fecha de novo pela mão).
   */
  autoCloseOn: z.string().nullable(),
});
export type PeriodMonth = z.infer<typeof periodMonthSchema>;

export const periodYearSchema = z.object({
  year: z.number().int(),
  settings: periodSettingsSchema,
  months: z.array(periodMonthSchema),
});
export type PeriodYear = z.infer<typeof periodYearSchema>;

export const periodYearQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
});

export const periodParamsSchema = z.object({
  organizationId: z.string().uuid(),
  year: z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(1).max(12),
});

/** Fechar: motivo opcional. */
export const periodCloseSchema = z.object({
  reason: z
    .string()
    .trim()
    .max(1000, 'Use no máximo 1000 caracteres.')
    .nullable()
    .optional()
    .transform((v) => v || null),
});
export type PeriodCloseInput = z.input<typeof periodCloseSchema>;

/** Reabrir: motivo obrigatório (o mês volta a aceitar alterações sem justificativa). */
export const periodReopenSchema = z.object({
  reason: z
    .string({ message: 'Explique por que o mês vai ser reaberto.' })
    .trim()
    .min(3, 'Explique por que o mês vai ser reaberto.')
    .max(1000, 'Use no máximo 1000 caracteres.'),
});
export type PeriodReopenInput = z.input<typeof periodReopenSchema>;

/** Último dia do mês ("AAAA-MM-DD"). */
export const monthEnd = (year: number, month: number) =>
  new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);

/**
 * Dia em que o mês fecha sozinho: passados N dias inteiros do fim do mês (com 10, março fecha em 11/04;
 * com 0, no dia 1º do mês seguinte).
 */
export function autoCloseDay(year: number, month: number, days: number): string {
  const d = new Date(Date.UTC(year, month, 0));
  d.setUTCDate(d.getUTCDate() + days + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Mês em que a meta "fecha" (o mês que decide se ela está em período fechado): o próprio mês, o último
 * mês do trimestre, dezembro no ano e o mês do acumulado.
 */
export function targetClosingMonth(periodType: TargetPeriodTypeName, period: number): number {
  switch (periodType) {
    case 'MONTH':
    case 'ACCUMULATED':
      return period;
    case 'QUARTER':
      return period * 3;
    case 'YEAR':
      return 12;
  }
}
